import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  Button,
  Tabs,
  Tab,
  Box,
  Typography,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Person as PersonIcon,
  Lock as LockIcon,
  Email as EmailIcon,
  Work as WorkIcon,
  LocalHospital as HospitalIcon,
} from '@mui/icons-material';
import { authManager, LoginCredentials, RegisterData } from '../utils/auth';

interface AuthModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  );
}

const AuthModal: React.FC<AuthModalProps> = ({ open, onClose, onSuccess }) => {
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Данные для входа
  const [loginData, setLoginData] = useState<LoginCredentials>({
    email: '',
    password: ''
  });

  // Данные для регистрации
  const [registerData, setRegisterData] = useState<RegisterData>({
    email: '',
    password: '',
    name: '',
    role: 'doctor',
    specialization: '',
    hospital: ''
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError('');
    setSuccess('');
  };

  const handleLogin = async () => {
    if (!loginData.email || !loginData.password) {
      setError('Заполните все поля');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await authManager.login(loginData);
      if (result.success) {
        setSuccess('Успешный вход в систему');
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 1000);
      } else {
        setError(result.error || 'Ошибка входа');
      }
    } catch (error) {
      setError('Произошла ошибка при входе');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!registerData.email || !registerData.password || !registerData.name) {
      setError('Заполните обязательные поля');
      return;
    }

    if (registerData.password.length < 6) {
      setError('Пароль должен содержать минимум 6 символов');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await authManager.register(registerData);
      if (result.success) {
        setSuccess('Регистрация прошла успешно');
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 1000);
      } else {
        setError(result.error || 'Ошибка регистрации');
      }
    } catch (error) {
      setError('Произошла ошибка при регистрации');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    setSuccess('');
    setLoginData({ email: '', password: '' });
    setRegisterData({
      email: '',
      password: '',
      name: '',
      role: 'doctor',
      specialization: '',
      hospital: ''
    });
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
        }
      }}
    >
      <DialogTitle sx={{ 
        textAlign: 'center', 
        pb: 1,
        background: 'linear-gradient(45deg, #4A90E2, #50C878)',
        color: 'white',
        borderRadius: '12px 12px 0 0'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <PersonIcon sx={{ fontSize: 28 }} />
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Вход в систему
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            centered
            sx={{
              '& .MuiTab-root': {
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem'
              }
            }}
          >
            <Tab label="Вход" />
            <Tab label="Регистрация" />
          </Tabs>
        </Box>

        {/* Вкладка входа */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ p: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Email"
              type="email"
              value={loginData.email}
              onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: <EmailIcon sx={{ mr: 1, color: '#4A90E2' }} />
              }}
            />

            <TextField
              fullWidth
              label="Пароль"
              type="password"
              value={loginData.password}
              onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: <LockIcon sx={{ mr: 1, color: '#4A90E2' }} />
              }}
            />

            <Button
              fullWidth
              variant="contained"
              onClick={handleLogin}
              disabled={isLoading}
              sx={{
                py: 1.5,
                background: 'linear-gradient(45deg, #4A90E2, #50C878)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #3A7BC8, #40B868)'
                }
              }}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Войти'}
            </Button>
          </Box>
        </TabPanel>

        {/* Вкладка регистрации */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ p: 3 }}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {success}
              </Alert>
            )}

            <TextField
              fullWidth
              label="Имя"
              value={registerData.name}
              onChange={(e) => setRegisterData({ ...registerData, name: e.target.value })}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: <PersonIcon sx={{ mr: 1, color: '#4A90E2' }} />
              }}
            />

            <TextField
              fullWidth
              label="Email"
              type="email"
              value={registerData.email}
              onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: <EmailIcon sx={{ mr: 1, color: '#4A90E2' }} />
              }}
            />

            <TextField
              fullWidth
              label="Пароль"
              type="password"
              value={registerData.password}
              onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: <LockIcon sx={{ mr: 1, color: '#4A90E2' }} />
              }}
            />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Роль</InputLabel>
              <Select
                value={registerData.role}
                onChange={(e) => setRegisterData({ ...registerData, role: e.target.value as 'doctor' | 'nurse' })}
                startAdornment={<WorkIcon sx={{ mr: 1, color: '#4A90E2' }} />}
              >
                <MenuItem value="doctor">Врач</MenuItem>
                <MenuItem value="nurse">Медсестра</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              label="Специализация"
              value={registerData.specialization}
              onChange={(e) => setRegisterData({ ...registerData, specialization: e.target.value })}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              label="Место работы"
              value={registerData.hospital}
              onChange={(e) => setRegisterData({ ...registerData, hospital: e.target.value })}
              sx={{ mb: 3 }}
              InputProps={{
                startAdornment: <HospitalIcon sx={{ mr: 1, color: '#4A90E2' }} />
              }}
            />

            <Button
              fullWidth
              variant="contained"
              onClick={handleRegister}
              disabled={isLoading}
              sx={{
                py: 1.5,
                background: 'linear-gradient(45deg, #4A90E2, #50C878)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #3A7BC8, #40B868)'
                }
              }}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Зарегистрироваться'}
            </Button>
          </Box>
        </TabPanel>
      </DialogContent>
    </Dialog>
  );
};

export default AuthModal;
